<template>
  <div v-if="visible" class="leitner-fab-root">
    <button
      type="button"
      class="leitner-fab"
      aria-label="افزودن واژه به جعبه لایتنر"
      title="لایتنر"
      @click="open = true"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke-linecap="round" />
      </svg>
      <span class="leitner-fab__label">لایتنر</span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="leitner-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leitner-add-title"
        @keydown.esc.prevent="close"
      >
        <button type="button" class="leitner-dialog__backdrop" aria-label="بستن" @click="close"></button>
        <div class="leitner-dialog__panel">
          <header class="leitner-dialog__header">
            <div>
              <p class="leitner-dialog__eyebrow">جعبه لایتنر</p>
              <h2 id="leitner-add-title" class="leitner-dialog__title">افزودن واژه جدید</h2>
            </div>
            <button type="button" class="leitner-dialog__close" aria-label="بستن" @click="close">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" stroke-linecap="round" />
              </svg>
            </button>
          </header>

          <form class="leitner-dialog__form" @submit.prevent="submit">
            <label class="leitner-dialog__field">
              <span>واژه <em>*</em></span>
              <input
                ref="wordInput"
                v-model="form.word"
                type="text"
                maxlength="120"
                placeholder="مثلاً accumulate"
                required
                autocomplete="off"
                dir="ltr"
              />
            </label>

            <label class="leitner-dialog__field">
              <span>معنی / تعریف <em>*</em></span>
              <textarea
                v-model="form.definition"
                rows="3"
                maxlength="2000"
                placeholder="تعریف واژه را بنویس"
                required
              ></textarea>
            </label>

            <label class="leitner-dialog__field">
              <span>مثال (اختیاری)</span>
              <textarea
                v-model="form.example"
                rows="2"
                maxlength="2000"
                placeholder="یک جمله نمونه"
                dir="ltr"
              ></textarea>
            </label>

            <div class="leitner-dialog__row">
              <label class="leitner-dialog__field">
                <span>تلفظ (اختیاری)</span>
                <input
                  v-model="form.phonetic"
                  type="text"
                  maxlength="120"
                  placeholder="/əˈkjuːmjəleɪt/"
                  dir="ltr"
                />
              </label>
              <label class="leitner-dialog__field">
                <span>نقش دستوری</span>
                <input
                  v-model="form.part_of_speech"
                  type="text"
                  maxlength="40"
                  placeholder="noun / verb / ..."
                  dir="ltr"
                />
              </label>
            </div>

            <p v-if="errorMessage" class="leitner-dialog__error" role="alert">
              {{ errorMessage }}
            </p>
            <p v-if="successMessage" class="leitner-dialog__success" role="status">
              {{ successMessage }}
            </p>

            <div class="leitner-dialog__actions">
              <button type="button" class="leitner-dialog__btn leitner-dialog__btn--ghost" @click="close">
                انصراف
              </button>
              <button
                type="submit"
                class="leitner-dialog__btn leitner-dialog__btn--primary"
                :disabled="submitting"
              >
                {{ submitting ? "در حال ذخیره..." : "افزودن به جعبه ۱" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { createLeitnerCard } from "@/api/leitner";
import { useAuth } from "@/composables/useAuth";
import { handleError } from "@/services/errorHandler";
import { tokenStorage } from "@/api/client";

const route = useRoute();
const { isAuthenticated: authFlag } = useAuth();

const open = ref(false);
const submitting = ref(false);
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);
const wordInput = ref<HTMLInputElement | null>(null);

const form = reactive({
  word: "",
  definition: "",
  example: "",
  phonetic: "",
  part_of_speech: "",
});

const isAuthenticated = computed(
  () => authFlag.value || !!tokenStorage.getAccess()
);

const isPublicAuthPage = computed(() => route.meta.public === true);

const visible = computed(() => isAuthenticated.value && !isPublicAuthPage.value);

watch(
  () => open.value,
  async (isOpen) => {
    if (!isOpen) return;
    errorMessage.value = null;
    successMessage.value = null;
    await nextTick();
    wordInput.value?.focus();
  }
);

function resetForm() {
  form.word = "";
  form.definition = "";
  form.example = "";
  form.phonetic = "";
  form.part_of_speech = "";
}

function close() {
  open.value = false;
  submitting.value = false;
  errorMessage.value = null;
  successMessage.value = null;
  resetForm();
}

async function submit() {
  if (submitting.value) return;
  submitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;
  try {
    await createLeitnerCard({
      word: form.word.trim(),
      definition: form.definition.trim(),
      example: form.example.trim() || null,
      phonetic: form.phonetic.trim() || null,
      part_of_speech: form.part_of_speech.trim() || null,
    });
    successMessage.value = `«${form.word.trim()}» به جعبه ۱ اضافه شد.`;
    resetForm();
    await nextTick();
    wordInput.value?.focus();
  } catch (e) {
    errorMessage.value = handleError(e, "LeitnerAddWord.submit");
  } finally {
    submitting.value = false;
  }
}

// Hide FAB on login/signup/forgot-password.
watch(
  isPublicAuthPage,
  (isPublic) => {
    if (isPublic) close();
  },
  { immediate: true }
);
</script>

<script lang="ts">
export default {
  name: "LeitnerAddWordFab",
};
</script>

<style scoped>
.leitner-fab-root {
  position: fixed;
  inset-inline-start: 20px;
  bottom: 24px;
  z-index: 1200;
  pointer-events: none;
}

.leitner-fab {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 999px;
  padding: 14px 18px;
  background: #6D5EF8;
  color: #fff;
  font-family: Vazirmatn, "Segoe UI", Tahoma, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(109, 94, 248, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.leitner-fab:hover {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow: 0 14px 32px rgba(109, 94, 248, 0.42);
}

.leitner-fab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(109, 94, 248, 0.3), 0 10px 28px rgba(109, 94, 248, 0.35);
}

.leitner-fab__label {
  line-height: 1;
}

.leitner-dialog {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  direction: rtl;
  font-family: Vazirmatn, "Segoe UI", Tahoma, sans-serif;
}

.leitner-dialog__backdrop {
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(44, 32, 24, 0.42);
  cursor: pointer;
}

.leitner-dialog__panel {
  position: relative;
  width: min(100%, 480px);
  max-height: min(92vh, 720px);
  overflow: auto;
  background: #FFFDF8;
  border: 1px solid #ECE4D9;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(76, 55, 42, 0.22);
  padding: 22px 22px 18px;
}

.leitner-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.leitner-dialog__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6A4328;
}

.leitner-dialog__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #4C372A;
}

.leitner-dialog__close {
  border: 1px solid #ECE4D9;
  background: #fff;
  border-radius: 999px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4C372A;
  cursor: pointer;
}

.leitner-dialog__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.leitner-dialog__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.leitner-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #4C372A;
}

.leitner-dialog__field em {
  color: #C45C5C;
  font-style: normal;
}

.leitner-dialog__field input,
.leitner-dialog__field textarea {
  width: 100%;
  border: 1px solid #ECE4D9;
  border-radius: 12px;
  background: #fff;
  padding: 11px 12px;
  font: inherit;
  font-weight: 500;
  color: #4C372A;
  box-sizing: border-box;
  resize: vertical;
}

.leitner-dialog__field input:focus,
.leitner-dialog__field textarea:focus {
  outline: none;
  border-color: #7C4DFF;
  box-shadow: 0 0 0 3px rgba(124, 77, 255, 0.16);
}

.leitner-dialog__error {
  margin: 0;
  color: #C45C5C;
  font-size: 13px;
  font-weight: 600;
}

.leitner-dialog__success {
  margin: 0;
  color: #3F8F4E;
  font-size: 13px;
  font-weight: 600;
}

.leitner-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.leitner-dialog__btn {
  border-radius: 12px;
  padding: 11px 16px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.leitner-dialog__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.leitner-dialog__btn--ghost {
  border: 1px solid #ECE4D9;
  background: #fff;
  color: #4C372A;
}

.leitner-dialog__btn--primary {
  border: none;
  background: #6D5EF8;
  color: #fff;
}

@media (max-width: 575.98px) {
  .leitner-fab-root {
    inset-inline-start: 14px;
    bottom: 16px;
  }

  .leitner-dialog__row {
    grid-template-columns: 1fr;
  }

  .leitner-fab__label {
    display: none;
  }

  .leitner-fab {
    width: 52px;
    height: 52px;
    padding: 0;
    justify-content: center;
  }
}
</style>
